import Dagre from '@dagrejs/dagre';
import { Edge, MarkerType, Node } from '@xyflow/react';

import { DomainMetadataTargetType, EnumModel, SchemaHierarchyModel, SchemaSummaryModel, SchemaTypesModel } from '../../api/api';
import { getDomainMetadataIdentity, getEnumNodeId } from './DomainGraphIdentity';

export type DomainGraphMetadata = {
	position?: {
		x: number;
		y: number;
	};
	domainGraph?: DomainGraphMetadata;
	[key: string]: unknown;
};

export type EntityNodeData = Record<string, unknown> & {
	kind: 'entity';
	entity: SchemaHierarchyModel;
	color: string;
	isAggregateRoot: boolean;
};

export type EnumNodeData = Record<string, unknown> & {
	kind: 'enum';
	enumModel: EnumModel;
	color: string;
};

export type DomainNodeData = EntityNodeData | EnumNodeData;

export type DomainMetadataUpdateItem = {
	id: string;
	metadata: string;
	targetType: DomainMetadataTargetType;
};

export type DomainGraphNodeFilter = 'root' | 'entity' | 'enum';

export const NODE_WIDTH = 260;
export const NODE_HEIGHT = 150;
export const ENUM_NODE_WIDTH = 220;
export const ENUM_NODE_HEIGHT = 120;
export const ROOT_COLORS = ['#2e7d32', '#1565c0', '#ad6c00', '#6a1b9a', '#00838f', '#c62828'];
export const FALLBACK_COLOR = '#546e7a';
export const ENUM_COLOR = '#f9a825';

export function parseMetadata(metadata?: string): DomainGraphMetadata {
	if (!metadata) {
		return {};
	}

	try {
		const parsed = JSON.parse(metadata);
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch {
		return {};
	}
}

export function serializePositionMetadata(metadata: string | undefined, position: { x: number; y: number }) {
	const parsed = parseMetadata(metadata);
	const { domainGraph: _domainGraph, ...rest } = parsed;
	return JSON.stringify({
		...rest,
		position,
	});
}

export function getStoredPosition(metadataSource: SchemaSummaryModel | SchemaHierarchyModel | EnumModel) {
	const metadata = parseMetadata(metadataSource.metadata);
	const position = metadata.position ?? metadata.domainGraph?.position;
	if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
		return undefined;
	}

	return position;
}

export function getNodeDimensions(node: Node<DomainNodeData>) {
	if (node.type === 'enum') {
		return { width: ENUM_NODE_WIDTH, height: ENUM_NODE_HEIGHT };
	}

	return { width: NODE_WIDTH, height: NODE_HEIGHT };
}

export function getNodeFilter(node: Node<DomainNodeData>): DomainGraphNodeFilter {
	if (node.data.kind === 'enum') {
		return 'enum';
	}

	return node.data.isAggregateRoot ? 'root' : 'entity';
}

export function getNodeName(node: Node<DomainNodeData>) {
	return node.data.kind === 'entity' ? node.data.entity.name : node.data.enumModel.name;
}

export function layoutNodes(nodes: Node<DomainNodeData>[], edges: Edge[], useStoredPositions = true) {
	const graph = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
	graph.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 130 });

	edges.forEach((edge) => graph.setEdge(edge.source, edge.target));
	nodes.forEach((node) => graph.setNode(node.id, getNodeDimensions(node)));

	Dagre.layout(graph);

	return nodes.map((node) => {
		const storedPosition = getStoredPosition(node.data.kind === 'entity' ? node.data.entity : node.data.enumModel);
		if (useStoredPositions && storedPosition) {
			return { ...node, position: storedPosition };
		}

		const graphNode = graph.node(node.id);
		const { width, height } = getNodeDimensions(node);
		return {
			...node,
			position: {
				x: graphNode.x - width / 2,
				y: graphNode.y - height / 2,
			},
		};
	});
}

export function getAggregateColors(entities: SchemaHierarchyModel[], outgoingReferences: Map<string, string[]>) {
	const rootIds = entities.filter((entity) => entity.schemaTypeId === SchemaTypesModel.RootDataSchema).map((entity) => entity.id);
	const colorByEntityId = new Map<string, string>();

	rootIds.forEach((rootId, index) => {
		const color = ROOT_COLORS[index % ROOT_COLORS.length];
		const pending = [rootId];

		while (pending.length > 0) {
			const currentId = pending.shift()!;
			if (colorByEntityId.has(currentId)) {
				continue;
			}

			colorByEntityId.set(currentId, color);
			for (const targetId of outgoingReferences.get(currentId) ?? []) {
				pending.push(targetId);
			}
		}
	});

	return colorByEntityId;
}

export function buildDomainGraph(entities: SchemaHierarchyModel[], enums: EnumModel[], selectedEntityId?: string, selectedEnumId?: string) {
	const entityById = new Map(entities.map((entity) => [entity.id, entity]));
	const enumById = new Map(enums.map((enumModel) => [enumModel.id, enumModel]));
	const outgoingReferences = new Map<string, string[]>();
	const edges: Edge[] = [];

	entities.forEach((entity) => {
		entity.properties
			.filter((property) => property.isReferenceProperty && property.referenceSchemaId && entityById.has(property.referenceSchemaId))
			.forEach((property) => {
				outgoingReferences.set(entity.id, [...(outgoingReferences.get(entity.id) ?? []), property.referenceSchemaId!]);
				edges.push({
					id: `${entity.id}-${property.name}-${property.referenceSchemaId}`,
					source: entity.id,
					target: property.referenceSchemaId!,
					label: property.name,
					type: 'smoothstep',
					markerEnd: {
						type: MarkerType.ArrowClosed,
						color: '#90a4ae',
					},
					style: {
						stroke: '#90a4ae',
						strokeWidth: 2,
					},
					labelStyle: {
						fill: '#cfd8dc',
						fontSize: 12,
					},
				});
			});

		entity.properties
			.filter((property) => property.isEnum && property.enumId && enumById.has(property.enumId))
			.forEach((property) => {
				const enumNodeId = getEnumNodeId(property.enumId!);
				edges.push({
					id: `${entity.id}-${property.name}-${enumNodeId}`,
					source: entity.id,
					target: enumNodeId,
					label: property.name,
					type: 'smoothstep',
					markerEnd: {
						type: MarkerType.ArrowClosed,
						color: ENUM_COLOR,
					},
					style: {
						stroke: ENUM_COLOR,
						strokeDasharray: '6 4',
						strokeWidth: 2,
					},
					labelStyle: {
						fill: '#ffe082',
						fontSize: 12,
					},
				});
			});
	});

	const colorByEntityId = getAggregateColors(entities, outgoingReferences);
	const entityNodes = entities.map<Node<EntityNodeData>>((entity) => ({
		id: entity.id,
		type: 'entity',
		position: { x: 0, y: 0 },
		selected: entity.id === selectedEntityId,
		data: {
			kind: 'entity',
			entity,
			color: colorByEntityId.get(entity.id) ?? FALLBACK_COLOR,
			isAggregateRoot: entity.schemaTypeId === SchemaTypesModel.RootDataSchema,
		},
	}));

	const enumNodes = enums.map<Node<EnumNodeData>>((enumModel) => ({
		id: getEnumNodeId(enumModel.id),
		type: 'enum',
		position: { x: 0, y: 0 },
		selected: enumModel.id === selectedEnumId,
		data: {
			kind: 'enum',
			enumModel,
			color: ENUM_COLOR,
		},
	}));

	const nodes: Node<DomainNodeData>[] = [...entityNodes, ...enumNodes];
	return { flowNodes: layoutNodes(nodes, edges), flowEdges: edges };
}

export function filterDomainGraph(flowNodes: Node<DomainNodeData>[], flowEdges: Edge[], enabledNodeFilters: DomainGraphNodeFilter[], searchText: string) {
	const normalizedSearchText = searchText.trim().toLocaleLowerCase();
	const visibleFlowNodes = flowNodes.filter((node) => {
		if (!enabledNodeFilters.includes(getNodeFilter(node))) {
			return false;
		}

		return !normalizedSearchText || getNodeName(node).toLocaleLowerCase().includes(normalizedSearchText);
	});
	const visibleNodeIds = new Set(visibleFlowNodes.map((node) => node.id));
	const visibleFlowEdges = flowEdges.filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target));

	return {
		visibleFlowNodes,
		visibleFlowEdges,
	};
}

export function withPositionMetadata(node: Node<DomainNodeData>) {
	const metadataSource = node.data.kind === 'entity' ? node.data.entity.metadata : node.data.enumModel.metadata;
	const metadata = serializePositionMetadata(metadataSource, node.position);
	return {
		...node,
		data: {
			...node.data,
			...(node.data.kind === 'entity' ? { entity: { ...node.data.entity, metadata } } : { enumModel: { ...node.data.enumModel, metadata } }),
		},
	};
}

export function toDomainMetadataUpdateItem(node: Node<DomainNodeData>): DomainMetadataUpdateItem {
	const identity = getDomainMetadataIdentity(node);
	if (node.data.kind === 'entity') {
		return {
			id: identity.id,
			targetType: identity.targetType,
			metadata: node.data.entity.metadata ?? '',
		};
	}

	return {
		id: identity.id,
		targetType: identity.targetType,
		metadata: node.data.enumModel.metadata ?? '',
	};
}
